import { useState, useEffect } from 'react';
import { getCachedAvatarUrl, getCachedAvatarUrlSync } from '../utils/avatarCache';

/**
 * Returns a URL safe for use in <img src> that avoids repeated requests to external hosts
 * (e.g. Google profile images). Resolves to a blob URL after the first fetch; subsequent
 * uses of the same URL get the cached blob URL.
 * @param {string|null|undefined} photoUrl - Original photo URL (e.g. from getPlayerPhotoUrl)
 * @returns {string|null} URL to use in img src, or null if no photoUrl
 */
export function useCachedAvatarUrl(photoUrl) {
  // Use only cached/blob or same-origin URL so we never set img src to raw external URL (avoids 429)
  const [resolvedUrl, setResolvedUrl] = useState(() => getCachedAvatarUrlSync(photoUrl) || null);

  useEffect(() => {
    if (!photoUrl) {
      setResolvedUrl(null);
      return;
    }
    const sync = getCachedAvatarUrlSync(photoUrl);
    if (sync) {
      setResolvedUrl(sync);
      return;
    }
    let cancelled = false;
    getCachedAvatarUrl(photoUrl).then((url) => {
      if (!cancelled && url) setResolvedUrl(url);
    });
    return () => { cancelled = true; };
  }, [photoUrl]);

  return resolvedUrl;
}
